import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from './cx.ts';

/**
 * Radix checkbox with an inline label. Checked state fills with the
 * signal accent; the box itself sits in a `well` like other fields.
 */
interface Props {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly label?: ReactNode;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly className?: string;
  readonly testId?: string;
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  disabled = false,
  id,
  className,
  testId,
}: Props): JSX.Element {
  const box = (
    <CheckboxPrimitive.Root
      id={id}
      checked={checked}
      onCheckedChange={(next) => onCheckedChange(next === true)}
      disabled={disabled}
      data-testid={testId}
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-line-strong bg-well transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 data-[state=checked]:border-signal data-[state=checked]:bg-signal disabled:pointer-events-none disabled:opacity-50"
    >
      <CheckboxPrimitive.Indicator>
        <Check aria-hidden="true" className="h-3 w-3 text-signal-ink" strokeWidth={3} />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );

  if (label === undefined || label === null) {
    return box;
  }

  return (
    <label
      className={cx(
        'inline-flex cursor-pointer select-none items-center gap-2 text-body text-paper-mid',
        disabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      {box}
      <span>{label}</span>
    </label>
  );
}
